import React, { useState, useEffect } from 'react';
import { FaPhone, FaEnvelope, FaFax, FaMapMarkerAlt, FaCheck } from 'react-icons/fa';
import emailjs from 'emailjs-com';
import { useInView } from '../hooks/useInView';

const ContactPage: React.FC = () => {
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const formSection = useInView();

  useEffect(() => {
    document.title = 'Contact Us | Janssen Law Professional Corporation';
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const templateParams = {
      name: formData.get('name'),
      email: formData.get('email'),
      message: formData.get('message'),
      phone: formData.get('phone'),
    };

    emailjs
      .send(
        'service_ban6o18',
        'template_hdn5xnr',
        templateParams,
        'E91OotIorBbdQESyu'
      )
      .then(
        () => {
          setFeedbackMessage('Your message has been sent. We will get back to you soon.');
          setFormSubmitted(true);
        },
        (error) => {
          console.error('Error sending email:', error);
          setFeedbackMessage('There was an error sending your message. Please try again.');
        }
      );
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <h1>Contact Us</h1>
        <p>Get in touch with our team for a consultation about your legal needs.</p>
      </section>

      <section className="contact-content">
        <div
          ref={formSection.ref}
          className={`contact-content-inner ${formSection.isVisible ? 'visible' : ''}`}
        >
          {/* Contact Info */}
          <div className="contact-info-section">
            <h2>Get In Touch</h2>
            <div className="info-cards">
              <div className="info-card">
                <div className="info-icon"><FaMapMarkerAlt /></div>
                <div className="info-text">
                  <h3>Office Address</h3>
                  <p>89 Scollard Street<br />Toronto, Canada M5R 1G4</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon"><FaPhone /></div>
                <div className="info-text">
                  <h3>Phone</h3>
                  <a href="tel:+14169291103">(416) 929-1103</a>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon"><FaFax /></div>
                <div className="info-text">
                  <h3>Fax</h3>
                  <p>(416) 929-9610</p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon"><FaEnvelope /></div>
                <div className="info-text">
                  <h3>Email</h3>
                  <a href="mailto:enquiry@janssen-law.com">enquiry@janssen-law.com</a>
                </div>
              </div>
            </div>

            <div className="office-hours">
              <h3>Office Hours</h3>
              <div className="hours-row">
                <span>Monday - Friday</span>
                <span>9:00 AM - 5:00 PM</span>
              </div>
              <div className="hours-row">
                <span>Saturday - Sunday</span>
                <span>Closed</span>
              </div>
            </div>
          </div>

          {/* Enquiry Form */}
          <div className="contact-form-section">
            <h2>Send Us a Message</h2>
            <div className="contact-form">
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number (optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="(416) 000-0000"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <select id="subject" name="subject">
                    <option value="">Select a practice area...</option>
                    <option value="Business Law">Business Law</option>
                    <option value="Real Estate Law">Real Estate Law</option>
                    <option value="Immigration Law">Immigration Law</option>
                    <option value="Wills & Estates">Wills & Estates</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your legal matter..."
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className={`submit-button ${formSubmitted ? 'sent' : ''}`}
                  disabled={formSubmitted}
                >
                  {formSubmitted ? (
                    <>
                      <FaCheck /> Message Sent
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
                {feedbackMessage && (
                  <div className="feedback-message">{feedbackMessage}</div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-map">
        <div className="contact-map-inner">
          <div className="map-container">
            <iframe
              title="Janssen Law Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.0!2d-79.3905!3d43.6725!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b34b5e1d3f0f7%3A0x0!2s89+Scollard+St%2C+Toronto%2C+ON+M5R+1G4!5e0!3m2!1sen!2sca!4v1"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
